import {
  ActionBar,
  AddressCard,
  ChakraFlex,
  DateFormat,
  DeviceOrderStatus,
  FAIconName,
  ModalV2,
  OrderProgressBar,
  SkeletonLoader,
} from '@equip.health/ui';

import {
  deriveAddressCardPropsfromBodyTraceOrderResponse,
  getDeviceStatus,
  getDeviceStatusForActive,
  getStatusHistory,
  getStatusHistoryDateByStatusLabel,
  isOrderCanceledOrFailed,
  isOrderPendingConfirmation,
  mapStatusHistoriesToStatusLabels,
} from '~/lib/util/bodyTraceTracking.util';
import { formattedDateFromISO } from '~/lib/util/date.util';
import { GetBodyTraceOrderResponse } from '~/store/services/clinicalData';
import { PatientClinicalEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

import { formatCellPhoneNumber } from '~/lib/util/patient.util';
import {
  EquipScaleOrderStatus,
  ORDER_PENDING_CONFIRMATION_TEXT,
  ORDERED_TEXT,
} from '~/lib/constants/patients.constants';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';

interface TrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bodyTraceOrderData: GetBodyTraceOrderResponse;
  isLoading?: boolean;
}
const TOP_LEVEL_ID = 'tracking-modal';

const EquipScaleTrackingModal = ({
  isOpen,
  onClose,
  bodyTraceOrderData,
  isLoading,
}: TrackingModalProps) => {
  const statusHistory = getStatusHistory(bodyTraceOrderData);
  const activeStates = mapStatusHistoriesToStatusLabels(statusHistory);
  const {
    addressLine1,
    addressLine2,
    city,
    state,
    zipcode,
    phoneNumber,
    email,
    name,
    addressTypeLabel,
  } = deriveAddressCardPropsfromBodyTraceOrderResponse(bodyTraceOrderData);
  const { trackEvent } = useAnalytics();

  const orderedBy = getDisplayNameOrLegalNameForProvider(
    bodyTraceOrderData?.createdByDisplayFirstName,
    bodyTraceOrderData?.createdByFirstName,
    bodyTraceOrderData?.createdByDisplayLastName,
    bodyTraceOrderData?.createdByLastName,
  );
  const trackingId = bodyTraceOrderData?.trackingNumber;
  const imei = bodyTraceOrderData?.device.id;
  const activatedEquipScaleTimestamp = bodyTraceOrderData?.device.dateActive;
  const formattedActivatedEquipScaleDate =
    activatedEquipScaleTimestamp &&
    formattedDateFromISO(
      activatedEquipScaleTimestamp,
      DateFormat.fullMonthDayYearWithTime,
    );

  const deliveredDate = getStatusHistoryDateByStatusLabel(
    statusHistory,
    'Delivered',
  );
  const orderedDate = getStatusHistoryDateByStatusLabel(
    statusHistory,
    'Requested',
  );
  const shippedDate = getStatusHistoryDateByStatusLabel(
    statusHistory,
    'Shipped',
  );
  const handleTrackingIdClick = () => {
    trackEvent(PatientClinicalEvents.trackingIdClicked, {
      source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
    });
    if (trackingId.startsWith('1Z') && trackingId.length === 18) {
      window.open(
        `https://www.ups.com/track?tracknum=${trackingId}`,
        '_blank',
        'noopener',
      );
    } else if (trackingId.startsWith('9') && trackingId.length === 22) {
      window.open(
        'https://m.usps.com/m/TrackConfirmAction_input',
        '_blank',
        'noopener',
      );
    } else {
      window.open(
        `https://www.google.com/search?q=${trackingId}`,
        '_blank',
        'noopener',
      );
    }
  };
  const deviceStatus = getDeviceStatus(bodyTraceOrderData);
  const activeDeviceStatus = getDeviceStatusForActive(bodyTraceOrderData);

  const activeStateList =
    isOrderPendingConfirmation(bodyTraceOrderData) &&
    activeDeviceStatus !== EquipScaleOrderStatus.ACTIVE
      ? [deviceStatus]
      : activeStates;

  const showActionBar = isOrderCanceledOrFailed(deviceStatus);
  let ordered: string;

  if (orderedDate) {
    ordered = orderedDate;
  } else if (activeDeviceStatus !== EquipScaleOrderStatus.ACTIVE) {
    ordered = ORDER_PENDING_CONFIRMATION_TEXT;
  } else {
    ordered = ORDERED_TEXT;
  }

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title="Tracking"
    >
      <SkeletonLoader id={TOP_LEVEL_ID} isLoaded={!isLoading}>
        <ChakraFlex flexDirection="column" gap="24px">
          {showActionBar && (
            <ActionBar
              barIcon={FAIconName.exclamationTriangleRegular}
              id={`${TOP_LEVEL_ID}__error-action-bar`}
              text="Please contact Product Support if you are facing issues with your Equip scale order."
              variant="critical"
            />
          )}

          <OrderProgressBar
            activeStates={activeStateList}
            id={`${TOP_LEVEL_ID}__progress-bar`}
            numStates={4}
          />
          <DeviceOrderStatus
            activated={formattedActivatedEquipScaleDate}
            avatarTooltipWrapper={({ children }) => (
              <ProviderAvatarTooltipWrapper
                providerExternalId={bodyTraceOrderData?.providerExternalId}
              >
                {children}
              </ProviderAvatarTooltipWrapper>
            )}
            avatarUrl={
              bodyTraceOrderData?.providerAvatarDetails?.thumbnailUrl ||
              bodyTraceOrderData?.providerAvatarDetails?.url
            }
            delivered={deliveredDate}
            id={`${TOP_LEVEL_ID}__device-order-status`}
            imei={imei}
            onClick={handleTrackingIdClick}
            ordered={ordered}
            orderedBy={orderedBy}
            shipped={shippedDate}
            trackingId={trackingId}
          />
          <AddressCard
            addressLine1={addressLine1}
            addressLine2={addressLine2}
            city={city}
            email={email}
            id={`${TOP_LEVEL_ID}__address-card`}
            name={name}
            phoneNumber={formatCellPhoneNumber(phoneNumber)}
            state={state}
            userRole={addressTypeLabel}
            zipCode={zipcode}
          />
        </ChakraFlex>
      </SkeletonLoader>
    </ModalV2>
  );
};

export default EquipScaleTrackingModal;
